<template>
  <v-container v-if='loaded'
    fill-height
    fluid
    grid-list-xl>

    <v-dialog v-model="openAuditTrail" max-width="1200px">
      <app-audit-trail-timeline entity="returned" v-if='openAuditTrail' :entity_id="returned_id" :fields='config.fields'/>
    </v-dialog>

    <v-flex>
      <app-upsert
        :config="config"
        ref='upsert'
        :item="returned"
        :title="returned.protocol && returned.protocol.name"
        :subtitle="`${returned.status} / ${returned.request_date}`"
        :hide-save="true">
      
        <template slot='headerActions'>
          <app-audit-trail-btn @click="openAuditTrail = true" />

          <app-patch v-if='returned.id && isAdmin' entity="returned" :entity_id="returned.id" :protocol_id="returned.protocol_id"/>

          <v-btn
            color="red"
            @click="$router.go(-1)">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>

        </template>
        <template slot="actions">
          <v-btn
            v-if="((user.role === 'Administrator' || user.role === 'EMN User' ) && returned.status !== 'CANCELED' )"
            class="mx-1 font-weight-light"
            color="primary"
            :loading="loading"
            :disabled="!nDrugs || loading || !returned._pdf"
            @click="saveNewPdf(returned)">SAVE NEW PDF</v-btn>

          <v-btn
            v-if="returned.status === 'INIT'"
            id="request_btn"
            class="mx-1 font-weight-light"
            color="primary"
            :loading="loading"
            :disabled="!nDrugs || loading"
            @click="request(returned)">REQUEST</v-btn>

          <v-btn
            v-if="returned.status === 'REQUESTED' && isAdmin"
            class="mx-1 red white--text font-weight-light"
            :loading="loading"
            :disabled="!nDrugs || loading"
            @click="cancel">CANCEL</v-btn>

          
            

          <v-btn
            v-if="returned.status === 'CONFIRMED' && user.role === 'Administrator'"
            class="mx-1 font-weight-light"
            color="primary"
            :disabled="loading"
            :loading="loading"
            @click="recreate">RECREATE PDF</v-btn>
          

          <v-menu offset-y
            v-if='returned.pdf'>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="purple darken-2 white--text"
                  v-bind="attrs"
                  v-on="on">
                  PDF
                  <v-icon right>mdi-file-pdf-box</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item
                  v-for="(item, index) in returnedPdfs"
                  :href='`${BASE_URL}/pdf/${encodeURIComponent(item)}`'
                  :key="index">
                  <v-list-item-title>{{ item }}</v-list-item-title>
                </v-list-item>
              </v-list>
          </v-menu>

          <v-btn
            v-if="canUpdate"
            class="mx-1 font-weight-light"
            color="primary"
            :loading="loading"
            :disabled="loading"
            @click="$refs.upsert.save()">UPDATE</v-btn>

          <v-btn
            v-if="returned.status === 'REQUESTED' && isAdmin"
            id="confirm_btn"
            class="mx-1 font-weight-light"
            color="primary"
            :loading="loading"
            :disabled="!nDrugs || loading"
            @click="confirm">CONFIRM</v-btn>

          <app-table
            :hide-actions="returned.status == 'CANCELED'"
            :hide-upsert="returned.status !== 'INIT'"
            :title="returned.status === 'INIT' ? 'Drugs to return' : 'Drugs returned'"
            :config="drugConfig"
            :filters="{ returned_id }"
            no-search
            @saved="saved"
            @loaded="dataLoaded"
            @partially="partiallyDetails"
            :hidden-fields="{ returned_id, protocol_id: returned.protocol_id, site_id: returned.site_id }" />

          <v-dialog
            persistent
            v-model='openPartially'
            max-width='750px'>
            <material-card
              title="Partially used details"
              :text="`${selectedPartially.drug && selectedPartially.drug.name + ' (' + selectedPartially.drug.packaging + ' ' + selectedPartially.drug.dose + selectedPartially.drug.measure_unit}) - ${selectedPartially.lotto}`" color='primary'>
              <v-form v-model='valid'
                class="mt-4">
                <v-data-table dense
                  :headers="partiallyHeaders"
                  :footer-props="{ 'items-per-page-options': [5, 10] }"
                  :items="partiallyItems">
                  <template v-slot:item.unused="props">
                    <v-text-field
                      v-model="props.item.unused"
                      label="n. Unused"
                      hide-details
                      type='number'
                      :rules="[validators.natural('n. Unused')]"
                      :disabled="returned.status !== 'INIT'"
                      single-line
                      autofocus
                    ></v-text-field>
                  </template>
                </v-data-table>

                <v-flex
                  xs12
                  text-right>
                  <!-- <v-btn class="mx-1 font-weight-light" color='warning' @click='openPartially=false'>CANCEL</v-btn> -->
                  <v-btn class="mx-1 font-weight-light"
                    color="primary"
                    v-if="returned.status === 'INIT'"
                    :disabled="!valid || !enablePartially || loading"
                    @click="confirmPartially">CONFIRM</v-btn>
                  <v-btn v-else @click="openPartially = false" color="warning">CLOSE</v-btn>
                </v-flex>
              </v-form>
            </material-card>
          </v-dialog>

        </template>

      </app-upsert>

    </v-flex>

  </v-container>
</template>

<script>

const BASE_URL = process.env.VUE_APP_BASE_API
import config from './config_index.js'
import drugConfig from './drug_index.js'
const { getDetail, getList, upsert } = require('@/api/base_entity')
const { cancelReturned, requestReturned, confirmReturned, refreshPdf } = require('@/api/returned')
import validators from '@/utils/validators'
import serialHelpers from '../../helpers'
import { mapState } from 'vuex'

export default {
  inheritAttrs: false,
  data () {
    return {
      validators,
      BASE_URL,
      config,
      drugConfig,
      returned_id: null,
      nDrugs: 0,
      drugs: [],
      returned: {},
      loaded: false,
      loading: false,
      openPartially: false,
      valid: false,
      selectedPartially: {},
      partiallyItems: [],
      partiallyHeaders: [
        { text: 'Serial n.', value: 'serial' },
        { text: 'N.unused', value: 'unused' }
      ],
      openAuditTrail: false
    }
  },
  async created () {
    this.returned_id = Number(this.$route.params.returnedId)
    this.returned = await getDetail('returned', { id: this.returned_id })
    this.loaded = true
  },
  computed: {
    ...mapState(['user']),
    isAdmin () {
      return this.user.role === 'Administrator' || this.user.role === 'EMN User'
    },    
    enablePartially () {
      return !this.partiallyItems.find(p => Number(p.unused) === 0 || p.unused === '')
    },
    returnedPdfs () {
      try {
        return JSON.parse(this.returned.pdf)
      } catch {
        return [this.returned.pdf]
      }
    },
    canUpdate(){
      return (this.isAdmin && this.returned.status !=='INIT')
    }
  },
  methods: {
    async saveNewPdf (item) {
      this.loading = true
      const formData = new FormData()
      formData.append('pdf', item['_pdf'])
      try {
        await upsert(`returned/save_new_pdf/${this.returned_id}`, formData)
        this.$router.go()
      } catch (e) {
        this.$root.$emit('openSnackbar', {
          message: 'An error occurred during update. ' +
            e && e.response ? e.response.data.message : 'Error',
          color: 'error'
        })
        this.loading = false        
      }
    },   
    async missingDetails () {
      // le drugs con dei partially used specificati devono
      // avere un corrispondente dettaglio specificato (solo bottle cps, blister e vials)
      const nPartiallyUsed = this.drugs.reduce((tot, item) => ['Bottle CPS', 'Blister', 'Vials'].includes(item.drug.packaging) ? tot + item.n_partially : tot, 0)
      const fullPartiallyUsed = await getList('partiallyused', { returned_id: this.returned_id }, { rowsPerPage: null }).then(ret => ret.data)
      const nDetails = fullPartiallyUsed.reduce((tot, p) => tot + ((Number(p.unused) > 0) ? 1 : 0), 0)
      return nDetails !== nPartiallyUsed //!this.partiallyItems.find(p => Number(p.unused) === 0 || p.unused === '')
    },
    async recreate () {
      this.loading = true
      await refreshPdf(this.returned.id)
      this.$router.go()
    },
    saved ({ item }) {
      if (item.n_partially && ['Bottle CPS', 'Blister', 'Vials'].includes(item.drug.packaging)) {
        this.partiallyDetails(item)
      }
    },
    async cancel () {
      this.loading = true
      try {
        await cancelReturned(this.returned)
        this.$router.go()
      } catch (e) {
        this.$root.$emit('openSnackbar', {
          message: `Error: ` + e.toString(),
          color: 'error'
        })
      }
      this.loading = false
    },
    async request (item) {
      if (!this.$refs.upsert.validate()) return
      this.loading = true
      // check if details are missing
      if (await this.missingDetails()) {
        this.$root.$emit('openSnackbar', {
          message: `Missing partially used details!`,
          color: 'error'
        })
        this.loading = false
        return
      }
      try {
        const formData = new FormData()
        formData.append('pdf', item['_pdf'])
        await requestReturned(this.returned.id, formData)
        this.$router.go()
      } catch (e) {
        this.loading = false
        this.$root.$emit('openSnackbar', {
          message: 'An error occurred during update. ' +
            e && e.response ? e.response.data.message : 'Error',
          color: 'error'
        })        
      }
    },
    async confirm () {
      this.loading = true
      // check if details are missing
      if (await this.missingDetails()) {
        this.$root.$emit('openSnackbar', {
          message: `Missing partially used details!`,
          color: 'error'
        })
        this.loading = false
        return
      }

      try {
        await confirmReturned(this.returned)
        this.$router.go()
      } catch (e) {
        this.$root.$emit('openSnackbar', {
          message: 'An error occurred during update. ' +
            e && e.response ? e.response.data.message : 'Error',
          color: 'error'
        })
        this.loading = false
      }
    },
    async confirmPartially () {
      const err = this.partiallyItems.find(p => p.unused >= this.selectedPartially.drug.n_pieces)
      if (err) {
        this.$root.$emit('openSnackbar', {
          message: `N.unused for serial ${err.serial} shoud be < of ${this.selectedPartially.drug.n_pieces} (${err.unused})`,
          color: 'error'
        })
        return
      }

      const updates = this.partiallyItems.map(p =>
       upsert('partiallyused', {
        returned_id: this.returned_id,
        lotto: this.selectedPartially.lotto,
        drug_id: this.selectedPartially.drug_id,
        protocol_id: this.returned.protocol_id,
        site_id: this.returned.site_id,
        serial: p.serial,
        unused: p.unused
      }))
      this.openPartially = false
      return Promise.all(updates)
    },
    async partiallyDetails (item) {
      this.selectedPartially = item

      // prepare what we need
      let partiallyItems = []
      // se ho i seriali 
      if (item.serials_partially && item.serials_partially.length) {
        partiallyItems = serialHelpers.explodeSerials(item.serials_partially).map(serial => ({ serial, unused: 0 }))
      } else {
        partiallyItems = Array.from({ length: item.n_partially }, (v, serial) => ({serial: serial+1, unused: 0}))
      }
      const serverPartiallyItems = await getList('partiallyused', {
        returned_id: this.returned_id,
        lotto: item.lotto,
        drug_id: item.drug_id 
      }, { rowsPerPage: null }).then(ret => ret.data)

      if (serverPartiallyItems) {
        this.partiallyItems = partiallyItems.map(i => {
          const serverItem = serverPartiallyItems.find(d => d.serial === String(i.serial))
          if (serverItem) {
            return { serial: i.serial, unused: serverItem.unused }
          }
          return i
        })
      } else {
        this.partiallyItems = partiallyItems
      }

      this.openPartially = true
    },
    dataLoaded (items) {
      this.nDrugs = items.length
      this.drugs = items
    }
  }
}
</script>
